"use client"

import type React from "react"
import { useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, Alert } from "reactstrap"
import { VehicleVisitService } from "../../../Service/TicketingService"
import VehiclePaymentModal from "./VehiclePaymentModal"
import Swal from "sweetalert2"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
}

const VehicleVerificationModal: React.FC<Props> = ({ isOpen, toggle, onSuccess }) => {
  const [plate, setPlate] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [verificationResult, setVerificationResult] = useState<any>(null)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!plate.trim()) {
      setError("Please enter a plate number")
      return
    }

    try {
      setLoading(true)
      setError("")

      const response = await VehicleVisitService.verifyVehicle(plate.trim().toUpperCase())

      if (response.success) {
        setVerificationResult(response)
        await Swal.fire({
          title: "Vehicle Verified!",
          text: `Parking fee: TSh ${response.amount.toLocaleString()}`,
          icon: "success",
          html: `
            <div class="text-start">
              <p><strong>Plate:</strong> ${response.plate}</p>
              <p><strong>Duration:</strong> ${response.duration_minutes} minutes</p>
              <p><strong>Amount:</strong> FRW ${response.amount.toLocaleString()}</p>
              <p><strong>Exhibition:</strong> ${response.exhibition}</p>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "Process Payment",
          cancelButtonText: "Close",
        }).then((result) => {
          if (result.isConfirmed) {
            setPaymentModalOpen(true)
          }
        })
      }
    } catch (error: any) {
      console.error("Failed to verify vehicle:", error)
      const errorMessage = error.response?.data?.message || "Verification failed"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentSuccess = () => {
    setPaymentModalOpen(false)
    setPlate("")
    setVerificationResult(null)
    onSuccess()
  }

  const handleClose = () => {
    setPlate("")
    setError("")
    setVerificationResult(null)
    toggle()
  }

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleClose} size="md">
        <ModalHeader toggle={handleClose}>Verify Vehicle Exit</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label for="plate">Plate Number *</Label>
              <Input
                name="plate"
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                placeholder="Enter vehicle plate number"
                autoFocus
              />
            </FormGroup>

            {error && (
              <Alert color="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <div className="text-end">
              <Button color="secondary" onClick={handleClose} className="me-2">
                Cancel
              </Button>
              <Button color="success" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Verify Vehicle"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <VehiclePaymentModal
        isOpen={paymentModalOpen}
        toggle={() => setPaymentModalOpen(false)}
        onSuccess={handlePaymentSuccess}
        verificationData={verificationResult}
      />
    </>
  )
}

export default VehicleVerificationModal
